import { Injectable, PLATFORM_ID, Signal, WritableSignal, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** Known beta features that can be toggled by the user. */
export const BETA_FEATURES = {
  flowblueprintEditor: 'flowblueprint-editor',
  flowActivityEditor: 'flow-activity-editor',
} as const;

export type BetaFeatureKey = (typeof BETA_FEATURES)[keyof typeof BETA_FEATURES];

const STORAGE_PREFIX = 'acit-beta:';

/**
 * Persists opt-in beta feature flags in localStorage so the choice survives across sessions.
 */
@Injectable({
  providedIn: 'root'
})
export class BetaFeatureService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly flags = new Map<BetaFeatureKey, WritableSignal<boolean>>();

  isEnabled(key: BetaFeatureKey): Signal<boolean> {
    return this.getFlag(key).asReadonly();
  }

  setEnabled(key: BetaFeatureKey, enabled: boolean): void {
    this.getFlag(key).set(enabled);
    if (!this.isBrowser) {
      return;
    }
    try {
      localStorage.setItem(STORAGE_PREFIX + key, String(enabled));
    } catch {
      // Storage can be unavailable (private mode / quota); keep the in-memory value.
    }
  }

  private getFlag(key: BetaFeatureKey): WritableSignal<boolean> {
    let flag = this.flags.get(key);
    if (!flag) {
      flag = signal(this.readStored(key));
      this.flags.set(key, flag);
    }
    return flag;
  }

  private readStored(key: BetaFeatureKey): boolean {
    if (!this.isBrowser) {
      return false;
    }
    try {
      return localStorage.getItem(STORAGE_PREFIX + key) === 'true';
    } catch {
      return false;
    }
  }
}
